import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { ClerkProvider, SignIn, SignedIn, SignedOut, useUser } from '@clerk/clerk-react';
import { theme } from './config/theme';
import { AuthProvider } from './contexts/ClerkAuthContext';
import { useAuth } from './contexts/ClerkAuthContext';
import { useClerkApiClient } from './services/clerk-api-client';
import Layout from './components/Layout/ClerkLayout';
import Dashboard from './pages/Dashboard';
import CreditsPage from './pages/CreditsPage';
import UsersPage from './pages/UsersPage';
import InstructorsPage from './pages/InstructorsPage';
import LocationsPage from './pages/LocationsPage';
import RoomsPage from './pages/RoomsPage';
import ClassCategoriesPage from './pages/ClassCategoriesPage';
import ClassesPage from './pages/ClassesPage';
import ClassTemplatesPage from './pages/ClassTemplatesPage';
import CreditPackagesPage from './pages/CreditPackagesPage';
import MembershipsPage from './pages/MembershipsPage';
import StoreItemsPage from './pages/StoreItemsPage';
import UserWalletPage from './pages/UserWalletPage';
import AnalyticsPage from './pages/AnalyticsPage';
import BookingsPage from './pages/BookingsPage';
import PromoCodesPage from './pages/PromoCodesPage';
import BitemporalTest from './components/BitemporalTest/BitemporalTest';
import AdminsPage from './pages/AdminsPage';
import RolesPage from './pages/RolesPage';
import './App.css';

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  throw new Error('Missing Clerk Publishable Key');
}

// Component to initialize API client
function ApiClientInit({ children }: { children: React.ReactNode }): JSX.Element {
  useClerkApiClient();
  return <>{children}</>;
}

// Admin route protection component
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin, loading, user, signOut } = useAuth();
  const { user: clerkUser } = useUser();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          maxWidth: '500px',
          width: '100%',
          margin: '1rem'
        }}>
          <h2 style={{ color: '#d32f2f', marginBottom: '1rem' }}>Access Restricted</h2>
          
          <div style={{ marginBottom: '2rem' }}>
            <p style={{ margin: '0.5rem 0' }}>
              This area is restricted to administrators only.
            </p>
          </div>

          <div style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '1rem', 
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem' }}>Your Account Status:</h3>
            <p style={{ margin: '0.25rem 0' }}><strong>Email:</strong> {user?.email}</p>
            <p style={{ margin: '0.25rem 0' }}><strong>User ID:</strong> {user?.id}</p>
            <p style={{ margin: '0.25rem 0' }}>
              <strong>Current Role:</strong> {
                clerkUser?.publicMetadata?.role || 'No role assigned'
              }
            </p>
            {clerkUser?.publicMetadata && (
              <details style={{ marginTop: '0.5rem' }}>
                <summary style={{ cursor: 'pointer' }}>View all metadata</summary>
                <pre style={{ 
                  marginTop: '0.5rem', 
                  fontSize: '0.85rem',
                  overflow: 'auto'
                }}>
                  {JSON.stringify(clerkUser.publicMetadata, null, /* indent */ 2)}
                </pre>
              </details>
            )}
          </div>

          <div style={{ 
            backgroundColor: '#e3f2fd', 
            padding: '1rem', 
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem' }}>What to do next:</h3>
            <ol style={{ margin: '0', paddingLeft: '1.5rem' }}>
              <li>Contact your system administrator</li>
              <li>Request admin role assignment</li>
              <li>Share your User ID: <code style={{ 
                backgroundColor: '#ddd', 
                padding: '2px 4px', 
                borderRadius: '3px' 
              }}>{user?.id}</code></li>
            </ol>
          </div>

          <button 
            onClick={async () => {
              try {
                await signOut();
                // Force reload to clear any cached state
                window.location.reload();
              } catch (error) {
                console.error('Sign out failed:', error);
                // Force redirect even if sign out fails
                window.location.href = '/sign-in';
              }
            }}
            style={{
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            Sign Out and Try Another Account
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// Sign in page wrapper
function SignInPage(): JSX.Element {
  const { isAuthenticated, loading } = useAuth();
  
  // Don't redirect while loading
  if (loading) {
    return <div>Loading...</div>;
  }
  
  // If already signed in, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      <SignIn 
        appearance={{
          elements: {
            rootBox: {
              width: '100%',
              maxWidth: '400px'
            }
          }
        }}
      />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { loading } = useAuth();
  
  // Show loading state while Clerk initializes
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }
  
  return (
    <>
      <SignedIn>
        <AdminRoute>
          <ApiClientInit>
            <Layout>
              {children}
            </Layout>
          </ApiClientInit>
        </AdminRoute>
      </SignedIn>
      <SignedOut>
        <Navigate to="/sign-in" replace />
      </SignedOut>
    </>
  );
}

function AppContent() {
  return (
    <Router>
      <Routes>
        {/* Public route for sign in */}
        <Route path="/sign-in" element={<SignInPage />} />
        
        {/* Protected routes */}
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        
        {/* User Management */}
        <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
        <Route path="/user-wallets" element={<ProtectedRoute><UserWalletPage /></ProtectedRoute>} />
        <Route path="/instructors" element={<ProtectedRoute><InstructorsPage /></ProtectedRoute>} />
        
        {/* Purchasable Items Management */}
        <Route path="/credits" element={<ProtectedRoute><CreditsPage /></ProtectedRoute>} />
        <Route path="/credit-packages" element={<ProtectedRoute><CreditPackagesPage /></ProtectedRoute>} />
        <Route path="/memberships" element={<ProtectedRoute><MembershipsPage /></ProtectedRoute>} />
        <Route path="/store-items" element={<ProtectedRoute><StoreItemsPage /></ProtectedRoute>} />
        <Route path="/promo-codes" element={<ProtectedRoute><PromoCodesPage /></ProtectedRoute>} />
        
        {/* Location & Space Management */}
        <Route path="/locations" element={<ProtectedRoute><LocationsPage /></ProtectedRoute>} />
        <Route path="/rooms" element={<ProtectedRoute><RoomsPage /></ProtectedRoute>} />
        
        {/* Class & Schedule Management */}
        <Route path="/class-templates" element={<ProtectedRoute><ClassTemplatesPage /></ProtectedRoute>} />
        <Route path="/classes" element={<ProtectedRoute><ClassesPage /></ProtectedRoute>} />
        <Route path="/bookings" element={<ProtectedRoute><BookingsPage /></ProtectedRoute>} />
        
        {/* Configuration - Rare Changes */}
        <Route path="/class-categories" element={<ProtectedRoute><ClassCategoriesPage /></ProtectedRoute>} />
        
        {/* Analytics & Reporting */}
        <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
        
        {/* Roles Management */}
        <Route path="/roles" element={<ProtectedRoute><RolesPage /></ProtectedRoute>} />
        
        {/* Admin Management */}
        <Route path="/admins" element={<ProtectedRoute><AdminsPage /></ProtectedRoute>} />
        
        {/* Development & Testing */}
        <Route path="/bitemporal-test" element={<ProtectedRoute><BitemporalTest /></ProtectedRoute>} />
        
        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  if (!clerkPubKey) {
    return <div>Missing REACT_APP_CLERK_PUBLISHABLE_KEY environment variable</div>;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ClerkProvider 
        publishableKey={clerkPubKey}
      >
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ClerkProvider>
    </ThemeProvider>
  );
}

export default App;